import { createParquetWorker } from './createWorker';
import { getParquetResourceId, ParquetMetricSink } from './instrumentation';
import { mergeParquetFiles } from './operations';
import { ParquetRequestError } from './requestError';
import {
  ParquetFile,
  ParquetMetadataReadRequest,
  ParquetRowsReadRequest,
  ParquetWorkerRequest,
  ParquetWorkerResponse,
} from './types';
import { chunkParquetRows } from './workerRuntime';

type WorkerFactory = () => Worker;
type YieldToMainThread = () => Promise<void>;

interface PendingRequest {
  id: number;
  kind: 'metadata' | 'rows';
  payload: ParquetMetadataReadRequest | (ParquetRowsReadRequest & { baseFile?: ParquetFile });
  resourceId: string;
  enqueuedAt: number;
  signal?: AbortSignal;
  resolve: (result: any) => void;
  reject: (error: Error) => void;
  removeAbortListener: () => void;
  generation?: number;
  responseChunks: Map<number, ParquetFile['rows']>;
  onProgress?: (loadedFraction: number) => void;
}

interface ParquetWorkerClientOptions {
  workerFactory?: WorkerFactory;
  metricSink?: ParquetMetricSink;
  instrumentationEnabled?: () => boolean;
  yieldToMainThread?: YieldToMainThread;
}

const createAbortError = () => new DOMException('Parquet parsing was aborted', 'AbortError');
const defaultYieldToMainThread = () => new Promise<void>((resolve) => setTimeout(resolve, 0));
const sanitizeClientError = (message?: string) =>
  (message || 'Parquet worker failed').replace(/https?:\/\/\S+/g, '[parquet-resource]');

export class ParquetWorkerClient {
  private nextRequestId = 1;

  private generation = 1;

  private queue: PendingRequest[] = [];

  private activeRequest?: PendingRequest;

  private worker: Worker;

  private readonly workerFactory: WorkerFactory;

  private readonly metricSink?: ParquetMetricSink;

  private readonly instrumentationEnabled: () => boolean;

  private readonly yieldToMainThread: YieldToMainThread;

  constructor({
    workerFactory = createParquetWorker,
    metricSink,
    instrumentationEnabled = () => Boolean(metricSink),
    yieldToMainThread = defaultYieldToMainThread,
  }: ParquetWorkerClientOptions = {}) {
    this.workerFactory = workerFactory;
    this.metricSink = metricSink;
    this.instrumentationEnabled = instrumentationEnabled;
    this.yieldToMainThread = yieldToMainThread;
    this.worker = this.createAndBindWorker();
  }

  readMetadata(request: ParquetMetadataReadRequest, signal?: AbortSignal) {
    return this.enqueue<ParquetFile['metadata']>('metadata', request, signal);
  }

  readRows(
    request: ParquetRowsReadRequest & { baseFile?: ParquetFile },
    signal?: AbortSignal,
    onProgress?: (loadedFraction: number) => void
  ) {
    return this.enqueue<ParquetFile>('rows', request, signal, onProgress);
  }

  terminate() {
    const error = new Error('Parquet worker client was terminated');
    this.queue.splice(0).forEach((request) => this.rejectRequest(request, error));
    if (this.activeRequest) this.rejectRequest(this.activeRequest, error);
    this.activeRequest = undefined;
    this.unbindAndTerminateWorker();
  }

  private enqueue<Result>(
    kind: PendingRequest['kind'],
    payload: PendingRequest['payload'],
    signal?: AbortSignal,
    onProgress?: PendingRequest['onProgress']
  ): Promise<Result> {
    return new Promise((resolve, reject) => {
      const id = this.nextRequestId++;
      const handleAbort = () => this.abortRequest(id);
      const request: PendingRequest = {
        id,
        kind,
        payload,
        resourceId: getParquetResourceId(payload.parquetFileUrl),
        enqueuedAt: performance.now(),
        signal,
        resolve,
        reject,
        removeAbortListener: () => signal?.removeEventListener('abort', handleAbort),
        responseChunks: new Map(),
        onProgress,
      };
      if (signal?.aborted) {
        reject(createAbortError());
        return;
      }
      signal?.addEventListener('abort', handleAbort, { once: true });
      this.queue.push(request);
      this.drain();
    });
  }

  private abortRequest(requestId: number) {
    const queuedIndex = this.queue.findIndex(({ id }) => id === requestId);
    if (queuedIndex >= 0) {
      const [request] = this.queue.splice(queuedIndex, 1);
      this.emitMetric(request, 'cancellation', 0);
      this.rejectRequest(request, createAbortError());
      return;
    }
    if (this.activeRequest?.id !== requestId) return;

    const request = this.activeRequest;
    this.activeRequest = undefined;
    this.emitMetric(request, 'cancellation', 0);
    this.rejectRequest(request, createAbortError());
    this.replaceWorker();
    this.drain();
  }

  private createAndBindWorker() {
    const worker = this.workerFactory();
    const generation = this.generation;
    worker.onmessage = (event: MessageEvent<ParquetWorkerResponse>) => this.handleMessage(generation, event.data);
    worker.onerror = (event: ErrorEvent) => this.handleWorkerError(generation, event);
    return worker;
  }

  private unbindAndTerminateWorker() {
    this.worker.onmessage = null;
    this.worker.onerror = null;
    this.worker.terminate();
  }

  private replaceWorker() {
    this.unbindAndTerminateWorker();
    this.generation += 1;
    this.worker = this.createAndBindWorker();
  }

  private async dispatch(request: PendingRequest) {
    request.generation = this.generation;
    this.emitMetric(request, 'queue-wait', performance.now() - request.enqueuedAt);
    const identity = { requestId: request.id, generation: request.generation };
    if (
      request.kind === 'rows' &&
      'baseFile' in request.payload &&
      request.payload.baseFile &&
      !request.payload.baseFile.columnarData
    ) {
      const { baseFile } = request.payload;
      const baseRowChunks = chunkParquetRows(baseFile.rows);
      for (let chunkIndex = 0; chunkIndex < baseRowChunks.length; chunkIndex += 1) {
        if (this.activeRequest !== request || request.signal?.aborted) return;
        const startedAt = performance.now();
        this.worker.postMessage({
          ...identity,
          kind: 'base-rows-chunk',
          chunkIndex,
          rows: baseRowChunks[chunkIndex],
        } satisfies ParquetWorkerRequest);
        this.emitMetric(request, 'base-row-delivery', performance.now() - startedAt, baseRowChunks[chunkIndex].length);
        await this.yieldToMainThread();
      }
    }
    if (this.activeRequest !== request || request.signal?.aborted) return;

    const payload =
      request.kind === 'rows' && 'baseFile' in request.payload && request.payload.baseFile
        ? request.payload.baseFile.columnarData
          ? { ...request.payload, baseFile: undefined }
          : { ...request.payload, baseFile: { ...request.payload.baseFile, rows: [] } }
        : request.payload;
    this.worker.postMessage({
      ...identity,
      kind: request.kind,
      payload,
      instrumentationEnabled: this.instrumentationEnabled(),
    } as ParquetWorkerRequest);
  }

  private handleMessage(workerGeneration: number, response: ParquetWorkerResponse) {
    const request = this.activeRequest;
    if (
      !request ||
      workerGeneration !== this.generation ||
      response.generation !== request.generation ||
      response.requestId !== request.id
    ) {
      return;
    }
    if (response.kind === 'metric') {
      this.metricSink?.(response.metric);
      return;
    }
    if (response.kind === 'progress') {
      if (response.totalRows > 0) request.onProgress?.(response.loadedRows / response.totalRows);
      return;
    }
    if (response.kind === 'rows-chunk') {
      const startedAt = performance.now();
      request.responseChunks.set(response.chunkIndex, response.rows);
      this.emitMetric(request, 'response-delivery', performance.now() - startedAt, response.rows.length);
      return;
    }
    if (response.kind === 'rows-complete') {
      void this.completeRowsRequest(request, response);
      return;
    }
    if (response.kind === 'columnar-complete') {
      this.activeRequest = undefined;
      const decodedFile: ParquetFile = {
        rows: [],
        columnarData: response.columnarData,
        metadata: response.metadata,
        columnsQueried: response.columnsQueried,
      };
      const baseFile = request.kind === 'rows' && 'baseFile' in request.payload ? request.payload.baseFile : undefined;
      this.resolveRequest(
        request,
        baseFile
          ? mergeParquetFiles({ existingData: baseFile, newData: decodedFile, resourceId: request.resourceId })
          : decodedFile
      );
      this.drain();
      return;
    }

    this.activeRequest = undefined;
    if (response.kind === 'metadata-success') this.resolveRequest(request, response.metadata);
    else if (response.kind === 'cancelled') this.rejectRequest(request, createAbortError());
    else
      this.rejectRequest(
        request,
        response.requestError
          ? new ParquetRequestError(response.requestError.status)
          : new Error(sanitizeClientError(response.message))
      );
    this.drain();
  }

  private async completeRowsRequest(
    request: PendingRequest,
    response: Extract<ParquetWorkerResponse, { kind: 'rows-complete' }>
  ) {
    const rows: ParquetFile['rows'] = [];
    const startedAt = performance.now();
    for (let chunkIndex = 0; chunkIndex < response.chunkCount; chunkIndex += 1) {
      const chunk = request.responseChunks.get(chunkIndex);
      if (!chunk) {
        this.activeRequest = undefined;
        this.rejectRequest(request, new Error(`Parquet worker response is missing chunk ${chunkIndex}`));
        this.drain();
        return;
      }
      rows.push(...chunk);
      await this.yieldToMainThread();
      if (this.activeRequest !== request) return;
    }
    this.emitMetric(request, 'response-accumulation', performance.now() - startedAt, rows.length);
    this.activeRequest = undefined;
    this.resolveRequest(request, { rows, metadata: response.metadata, columnsQueried: response.columnsQueried });
    this.drain();
  }

  private handleWorkerError(workerGeneration: number, event: ErrorEvent) {
    if (workerGeneration !== this.generation) return;
    const request = this.activeRequest;
    this.activeRequest = undefined;
    if (request) this.rejectRequest(request, new Error(sanitizeClientError(event.message)));
    this.emitMetric(request, 'worker-restart', 0);
    this.replaceWorker();
    this.drain();
  }

  private resolveRequest(request: PendingRequest, result: unknown) {
    request.removeAbortListener();
    request.responseChunks.clear();
    request.resolve(result);
  }

  private rejectRequest(request: PendingRequest, error: Error) {
    request.removeAbortListener();
    request.responseChunks.clear();
    request.reject(error);
  }

  private emitMetric(
    request: PendingRequest | undefined,
    stage:
      | 'queue-wait'
      | 'base-row-delivery'
      | 'response-delivery'
      | 'response-accumulation'
      | 'cancellation'
      | 'worker-restart',
    durationMs: number,
    rowCount?: number
  ) {
    if (!request || !this.metricSink || !this.instrumentationEnabled()) return;
    const columnCount = 'columnsForQuery' in request.payload ? request.payload.columnsForQuery?.length : undefined;
    this.metricSink({ resourceId: request.resourceId, stage, thread: 'main', durationMs, rowCount, columnCount });
  }

  private drain() {
    if (this.activeRequest) return;
    const request = this.queue.shift();
    if (!request) return;
    if (request.signal?.aborted) {
      this.rejectRequest(request, createAbortError());
      this.drain();
      return;
    }
    this.activeRequest = request;
    void this.dispatch(request).catch((error) => {
      if (this.activeRequest !== request) return;
      this.activeRequest = undefined;
      this.rejectRequest(request, new Error(sanitizeClientError(error instanceof Error ? error.message : undefined)));
      this.replaceWorker();
      this.drain();
    });
  }
}
